<template>
  <main class="main">
    <section class="product" v-if="project">
      <Breadcrumbs :breadcrumbs="breadcrumbs" />

      <div class="container">
        <div class="product__wrapper">
          <div class="product-card">
            <div class="product-card__wrapper">

              <!-- Слайдер фото проекта -->
              <div class="product-card__slider product-card-slider">
                <div class="product-card-slider__swiper swiper">
                  <div class="product-card-slider__wrapper swiper-wrapper">
                    <div class="product-card-slider__slide swiper-slide">
                      <Card :slider="slider" />
                    </div>
                  </div>
                </div>
              </div>

              <!-- Контент -->
              <div class="product-card__content">
                <h3 class="product-card__title title">{{ project.name }}</h3>

                <div class="re-project__meta">
                  <span v-if="statusLabel" class="re-project__status">{{ statusLabel }}</span>
                  <span v-if="developerName" class="re-project__developer">{{ developerName }}</span>
                </div>

                <div v-if="project.tagline" class="re-project__tagline">{{ project.tagline }}</div>

                <button class="re-cta-btn" @click="showInquiry = true">
                  {{ project.ctaTitle || 'Связаться с застройщиком' }}
                </button>

                <div class="product-card__info-wrapper desktop">
                  <div class="product-card__info info re-project__desc" v-html="project.description" />
                </div>
              </div>
            </div>

            <!-- Описание (мобилка) -->
            <div class="product-card__info-wrapper mobile">
              <div class="product-card__info info" v-html="project.description" />
            </div>

            <!-- Длинная статья -->
            <div v-if="project.articleText" class="re-project__article">
              <div class="info" v-html="project.articleText" />
            </div>
          </div>

          <!-- ── Блоки ниже карточки ── -->
          <section v-if="infraItems.length || objects.length" class="vibe-realize">

            <!-- Инфраструктура: горизонтальный слайдер -->
            <div v-if="infraItems.length" class="vibe-realize__block">
              <h2 class="title center mb">Инфраструктура</h2>
              <Swiper
                :modules="[SwiperNavigation]"
                :slides-per-view="1.2"
                :space-between="16"
                :navigation="true"
                :breakpoints="{
                  640:  { slidesPerView: 2.2, spaceBetween: 20 },
                  1024: { slidesPerView: 3.2, spaceBetween: 24 },
                  1280: { slidesPerView: 4,   spaceBetween: 24 },
                }"
                class="re-infra-swiper"
              >
                <SwiperSlide v-for="item in infraItems" :key="item.id">
                  <div class="vibe-transport__card">
                    <div class="vibe-transport__image">
                      <img
                        v-if="item.previewImage"
                        :src="fixUrl(item.previewImage.path)"
                        :alt="item.name"
                      />
                      <div v-else class="vibe-transport__placeholder">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                          <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"/>
                          <polyline points="9 22 9 12 15 12 15 22"/>
                        </svg>
                      </div>
                    </div>
                    <div class="vibe-transport__info">
                      <span v-if="item.categoryCode" class="vibe-transport__brand">{{ item.categoryCode }}</span>
                      <span class="vibe-transport__model">{{ item.name }}</span>
                      <span v-if="item.description" class="re-infra__desc">{{ item.description }}</span>
                    </div>
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>

            <!-- Объекты -->
            <div v-if="objects.length" class="vibe-realize__block">
              <h3 class="vibe-realize__subtitle">Объекты в проекте</h3>
              <div class="vibe-re__grid">
                <NuxtLink
                  v-for="obj in objects"
                  :key="obj.id"
                  :to="`/real-estate/${obj.id}`"
                  class="vibe-re__card"
                >
                  <div class="vibe-re__image">
                    <img
                      v-if="obj.mainImage"
                      :src="fixUrl(obj.mainImage.path)"
                      :alt="obj.name"
                    />
                    <div v-else class="vibe-re__placeholder">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.2">
                        <path d="M3 21V7l9-4 9 4v14M9 21v-6h6v6M12 3v18"/>
                      </svg>
                    </div>
                  </div>
                  <div class="vibe-re__info">
                    <span class="vibe-re__label">{{ obj.propertyType || 'Объект' }}</span>
                    <span class="vibe-re__name">{{ obj.name }}</span>
                  </div>
                </NuxtLink>
              </div>
            </div>

          </section>
        </div>
      </div>
    </section>

    <Spinner v-else />

    <RealEstateInquiryModal
      v-if="showInquiry"
      :title="project?.ctaTitle || 'Связаться с застройщиком'"
      :projectId="project?.id"
      @close="showInquiry = false"
    />
  </main>
</template>

<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation as SwiperNavigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'
import Card from '~/components/UI/Card/Card.vue'
import Breadcrumbs from '~/components/Section/Breadcrumbs.vue'
import Spinner from '~/components/animate/SpinnerComponent.vue'
import RealEstateInquiryModal from '~/components/Modals/RealEstateInquiry.vue'
import { RealEstateProject as RealEstateProjectApi } from '~/api/RealEstate'

const showInquiry = ref(false)

const route = useRoute()
const id = Number(route.params.id)

let project: any = null
let infraItems: any[] = []
let objects: any[] = []

try {
  project = await RealEstateProjectApi.getOne(id)
} catch {}

if (project) {
  try {
    const [infraRes, objRes] = await Promise.all([
      RealEstateProjectApi.getInfraByProject(id),
      RealEstateProjectApi.getObjectsByProject(id),
    ])
    infraItems = (infraRes?.items ?? []).filter((i: any) => i.status !== 'archived')
    objects = objRes?.objects ?? []
  } catch {}
}

const fixUrl = (url?: string) => (url ?? '').replaceAll('\\', '/')

const slider = (project?.files ?? [])
  .filter((f: any) => f.isImage)
  .map((f: any) => ({ id: f.id, src: fixUrl(f.path) }))

const statusLabels: Record<string, string> = {
  planned:   'Планируется',
  building:  'Строится',
  completed: 'Сдан',
  sold_out:  'Продано',
}
const statusLabel = statusLabels[project?.status] ?? project?.status ?? ''
const developerName = project?.developer?.name || project?.developerName || ''

const breadcrumbs = [
  { title: 'Главная', url: '/' },
  { title: 'Недвижимость', url: '/real-estate' },
  { title: project?.name ?? 'Проект', url: null },
]

useHead({
  title: project?.metaTitle || project?.name || 'Проект',
  meta: [{ name: 'description', content: project?.metaDescription || project?.description || '' }],
})
</script>

<style scoped>
.re-project__desc {
  max-width: 70% !important;
}
@media (max-width: 768px) {
  .re-project__desc { max-width: 100% !important; }
}

.re-project__meta {
  display: flex;
  align-items: center;
  gap: 1.2rem;
  margin-bottom: 1.6rem;
}
.re-project__status {
  display: inline-block;
  padding: 3px 10px;
  border: 1px solid #ccc;
  border-radius: 2px;
  font-size: 1rem;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: #888;
}
.re-project__developer {
  font-size: 1.2rem;
  color: #999;
}
.re-project__tagline {
  font-size: 1.5rem;
  color: #555;
  font-weight: 300;
  margin-bottom: 2rem;
  line-height: 1.5;
}
.re-project__article {
  margin-top: 4rem;
  padding-top: 4rem;
  border-top: 1px solid #f0f0f0;
  max-width: 70%;
  margin-left: auto;
  margin-right: auto;
}

.re-project__article :deep(p) {
  font-size: 1.7rem;
  font-weight: 400;
  line-height: 1.8;
  color: #555;
  margin-bottom: 1.6rem;
}

.re-project__article :deep(h2) {
  font-size: 3rem;
  font-weight: 700;
  color: #1B1918;
  line-height: 1.2;
  margin: 3.6rem 0 1.2rem;
}

.re-project__article :deep(h3) {
  font-size: 2.4rem;
  font-weight: 700;
  color: #1B1918;
  line-height: 1.25;
  margin: 3.2rem 0 1rem;
}

.re-project__article :deep(h4) {
  font-size: 1.9rem;
  font-weight: 700;
  color: #1B1918;
  line-height: 1.3;
  margin: 2.8rem 0 0.8rem;
}

.re-project__article :deep(h5),
.re-project__article :deep(h6) {
  font-size: 1.6rem;
  font-weight: 600;
  color: #1B1918;
  margin: 2.4rem 0 0.6rem;
}

.re-project__article :deep(strong) {
  font-weight: 600;
  color: #1B1918;
}

.re-project__article :deep(ul),
.re-project__article :deep(ol) {
  margin: 0 0 1.6rem 2rem;
}

.re-project__article :deep(li) {
  font-size: 1.7rem;
  font-weight: 400;
  line-height: 1.8;
  color: #555;
  margin-bottom: 0.6rem;
}

.re-project__article :deep(ul li) {
  list-style-type: disc;
}

.re-project__article :deep(ol li) {
  list-style-type: decimal;
}

.re-project__article :deep(blockquote) {
  border-left: 3px solid #1B1918;
  padding-left: 2rem;
  margin: 2.4rem 0;
  color: #777;
  font-size: 1.8rem;
  font-weight: 300;
  line-height: 1.7;
}

.re-project__article :deep(a) {
  color: #1B1918;
  text-decoration: underline;
  text-underline-offset: 3px;
}

@media (max-width: 768px) {
  .re-project__article {
    max-width: 100%;
  }
  .re-project__article :deep(h2) { font-size: 2.4rem; }
  .re-project__article :deep(h3) { font-size: 2rem; }
  .re-project__article :deep(h4) { font-size: 1.7rem; }
}

.re-cta-btn {
  display: inline-block;
  margin-top: 2rem;
  margin-bottom: 2rem;
  padding: 1.4rem 3rem;
  background: #1B1918;
  color: #fff;
  border: none;
  border-radius: 2px;
  font-size: 1.3rem;
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: opacity 0.2s;
}
.re-cta-btn:hover { opacity: 0.8; }

/* Infra swiper */
.re-infra-swiper {
  overflow: visible;
  padding-bottom: 1rem;
}
:deep(.re-infra-swiper .swiper-button-prev),
:deep(.re-infra-swiper .swiper-button-next) {
  color: #1a1a1a;
  top: 35%;
}
:deep(.re-infra-swiper .swiper-button-prev::after),
:deep(.re-infra-swiper .swiper-button-next::after) {
  font-size: 18px;
}

.re-infra__desc {
  font-size: 1.2rem;
  color: #999;
  line-height: 1.5;
  margin-top: 0.4rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* reuse vibe styles for grid/cards */
.vibe-realize {
  margin-top: 6rem;
}
.vibe-realize__block {
  margin-bottom: 4rem;
}
.vibe-realize__subtitle {
  font-size: 1.1rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #888;
  margin: 0 0 2rem;
}
.vibe-transport__card {
  background: #f7f7f5;
  border-radius: 12px;
  overflow: hidden;
}
.vibe-transport__image {
  width: 100%;
  aspect-ratio: 3/4;
  overflow: hidden;
  background: #ebebeb;
}
.vibe-transport__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.vibe-transport__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
}
.vibe-transport__placeholder svg { width: 48px; height: 48px; }
.vibe-transport__info {
  padding: 1.6rem 1.8rem 2rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.vibe-transport__brand {
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #999;
}
.vibe-transport__model {
  font-size: 1.6rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.2;
}
.vibe-re__grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
@media (max-width: 900px) {
  .vibe-re__grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .vibe-re__grid { grid-template-columns: 1fr; }
}
.vibe-re__card {
  display: block;
  text-decoration: none;
  color: inherit;
  border-radius: 12px;
  overflow: hidden;
  background: #f7f7f5;
  transition: transform 0.2s ease;
}
.vibe-re__card:hover { transform: translateY(-3px); }
.vibe-re__image {
  width: 100%;
  aspect-ratio: 4/3;
  overflow: hidden;
  background: #ebebeb;
}
.vibe-re__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.vibe-re__placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #bbb;
}
.vibe-re__placeholder svg { width: 48px; height: 48px; }
.vibe-re__info {
  padding: 1.4rem 1.6rem 1.8rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}
.vibe-re__label {
  font-size: 1.1rem;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #999;
}
.vibe-re__name {
  font-size: 1.6rem;
  font-weight: 500;
  color: #1a1a1a;
  line-height: 1.3;
}
</style>
